type GetPriorityFn<T> = (item: T) => number | null | undefined;

export const sortByPriority = <T>(
    getPriority: GetPriorityFn<T>,
    items: T[] = []
): T[] => {
    return items
        .map((item, index) => ({
            item,
            index,
            priority: getPriority(item),
        }))
        .sort((a, b) => {
            const hasPriorityA =
                typeof a.priority === "number" && !Number.isNaN(a.priority);
            const hasPriorityB =
                typeof b.priority === "number" && !Number.isNaN(b.priority);

            if (hasPriorityA && hasPriorityB) {
                return (a.priority as number) - (b.priority as number);
            }
            if (hasPriorityA) return -1;
            if (hasPriorityB) return 1;
            return a.index - b.index;
        })
        .map(({ item }) => item);
};

/**
 * Ordina i tour usando tourPriorities dalla risposta API
 * @param tours Array di tour da ordinare
 * @param tourPriorities Array di oggetti { tourId: string, priority: number } dalla risposta API
 * @returns Array di tour ordinati per priorità decrescente (4, 3, 2, 1...). I tour senza priorità vengono messi alla fine.
 */
export const sortToursByPriorities = <T extends { _id: string }>(
    tourPriorities: Array<{ tourId: string; priority: number }> | undefined,
    tours: T[] = []
): T[] => {
    if (!tourPriorities || tourPriorities.length === 0) {
        return tours;
    }

    // Crea mappa priorità
    const priorityMap = new Map<string, number>();
    tourPriorities.forEach(({ tourId, priority }) => {
        priorityMap.set(tourId, priority);
    });

    // Ordina i tour
    return [...tours].sort((a, b) => {
        // eslint-disable-next-line no-underscore-dangle
        const priorityA = priorityMap.get(a._id) ?? -1; // fallback per tour senza priorità (va alla fine)
        // eslint-disable-next-line no-underscore-dangle
        const priorityB = priorityMap.get(b._id) ?? -1;
        return priorityB - priorityA; // ordine decrescente (priorità più alta prima)
    });
};

/**
 * Ordina le destinazioni per priorità decrescente (priorità più alta = numero più alto = mostrato per primo)
 * @param destinations Array di destinazioni da ordinare
 * @returns Array di destinazioni ordinato per priorità decrescente. Le destinazioni senza priorità vengono messe alla fine.
 */
export const sortDestinationsByPriority = <T extends { priority?: number }>(
    destinations: T[] = []
): T[] => {
    if (!destinations || destinations.length === 0) {
        return destinations;
    }

    // Verifica se almeno una destinazione ha il campo priority
    const hasAnyPriority = destinations.some(
        d => typeof d.priority === "number"
    );

    // Se nessuna destinazione ha priorità, ritorna l'array originale (mantiene l'ordine del backend)
    if (!hasAnyPriority) {
        return destinations;
    }

    return [...destinations].sort((a, b) => {
        const priorityA = a.priority ?? 0; // fallback per destinazioni senza priorità
        const priorityB = b.priority ?? 0;
        // Se entrambe hanno priorità 0 (nessuna priorità), mantieni l'ordine originale
        if (priorityA === 0 && priorityB === 0) {
            return 0;
        }
        return priorityB - priorityA; // ordine decrescente (priorità più alta prima)
    });
};
